import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { fetchKpis, listWorkOrders, listAssets } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Wrench,
  Activity,
  ArrowUpRight,
  CalendarClock,
} from "lucide-react";
import { StatusPill, TypePill, PriorityPill } from "@/components/Pills";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";

const KpiCard = ({ icon: Icon, label, value, accent, sub, testid }) => (
  <div className="engineered-card p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
          {label}
        </div>
        <div className="font-display font-extrabold text-3xl text-zinc-950 mt-2 font-mono">
          {value}
        </div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
      </div>
      <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [k, setK] = useState(null);
  const [recent, setRecent] = useState([]);
  const [critical, setCritical] = useState([]);

  useEffect(() => {
    (async () => {
      const [kpis, wos, assets] = await Promise.all([
        fetchKpis(),
        listWorkOrders(),
        listAssets(),
      ]);
      setK(kpis);
      setRecent(wos.slice(0, 6));
      setCritical(
        assets
          .filter((a) => a.criticality === "critical" || a.health < 75)
          .slice(0, 5)
      );
    })();
  }, []);

  const statusChart = k
    ? [
        { name: "Open", v: k.by_status.open, c: "#3b82f6" },
        { name: "In Prog.", v: k.by_status.in_progress, c: "#f97316" },
        { name: "On Hold", v: k.by_status.on_hold, c: "#eab308" },
        { name: "Done", v: k.by_status.completed, c: "#22c55e" },
        { name: "Cancel", v: k.by_status.cancelled, c: "#a1a1aa" },
      ]
    : [];

  return (
    <Layout title={`Welcome back, ${user?.name?.split(" ")[0] || "Operator"}`} breadcrumbs={["Operations"]}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <KpiCard
          testid="kpi-open-wo"
          icon={ClipboardList}
          label="Open Work Orders"
          value={k?.open_work_orders ?? "—"}
          sub={`${k?.in_progress ?? 0} in progress`}
          accent="bg-blue-50 text-blue-700"
        />
        <KpiCard
          testid="kpi-overdue"
          icon={AlertTriangle}
          label="Overdue"
          value={k?.overdue ?? "—"}
          sub={`${k?.overdue_ppm ?? 0} overdue PPM`}
          accent="bg-red-50 text-red-700"
        />
        <KpiCard
          testid="kpi-completion"
          icon={CheckCircle2}
          label="Completion Rate"
          value={`${k?.completion_rate ?? 0}%`}
          sub={`${k?.completed ?? 0} / ${k?.total ?? 0} completed`}
          accent="bg-emerald-50 text-emerald-700"
        />
        <KpiCard
          testid="kpi-health"
          icon={Activity}
          label="Avg Asset Health"
          value={`${k?.avg_asset_health ?? 0}%`}
          sub={`${k?.assets_count ?? 0} assets tracked`}
          accent="bg-orange-50 text-orange-700"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Status chart */}
        <div className="engineered-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                Operations
              </div>
              <h3 className="font-display font-bold text-xl text-zinc-950">
                Work Order Status Distribution
              </h3>
            </div>
            <Link
              to="/work-orders"
              data-testid="link-view-all-wo"
              className="text-xs font-bold tracking-wider uppercase text-orange-600 hover:text-orange-700 flex items-center gap-1"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={statusChart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "#fafafa" }}
                  contentStyle={{
                    borderRadius: 2,
                    border: "1px solid #e4e4e7",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="v" radius={[2, 2, 0, 0]}>
                  {statusChart.map((entry, i) => (
                    <Cell key={i} fill={entry.c} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 border-t border-zinc-200 mt-2 pt-4">
            <Mini label="Preventive (PM)" v={k?.by_type?.PM ?? 0} />
            <Mini label="Corrective (CM)" v={k?.by_type?.CM ?? 0} />
            <Mini label="Technicians" v={k?.technicians ?? 0} />
          </div>
        </div>

        {/* Asset by category */}
        <div className="engineered-card p-5">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
            Asset Mix
          </div>
          <h3 className="font-display font-bold text-xl text-zinc-950 mb-4">
            By Category
          </h3>
          <div className="space-y-3">
            <CatBar label="HVAC" value={k?.by_category?.hvac ?? 0} total={k?.assets_count ?? 1} color="bg-blue-500" />
            <CatBar label="Equipment" value={k?.by_category?.equipment ?? 0} total={k?.assets_count ?? 1} color="bg-orange-500" />
            <CatBar label="Civil Work" value={k?.by_category?.civil ?? 0} total={k?.assets_count ?? 1} color="bg-emerald-500" />
          </div>
          <div className="border-t border-zinc-200 mt-5 pt-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">
              Critical Assets
            </div>
            {critical.length === 0 && <div className="text-sm text-zinc-500">All clear</div>}
            <div className="space-y-2">
              {critical.map((a) => (
                <Link
                  to={`/assets`}
                  key={a.id}
                  className="flex items-center justify-between text-sm hover:bg-zinc-50 px-2 py-1.5 -mx-2 rounded-sm"
                >
                  <span className="font-mono text-xs text-zinc-500">{a.tag}</span>
                  <span className="flex-1 mx-2 truncate">{a.name}</span>
                  <span className={`text-xs font-bold ${a.health < 70 ? "text-red-600" : a.health < 85 ? "text-orange-600" : "text-emerald-700"}`}>
                    {a.health}%
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent work orders */}
      <div className="engineered-card mt-6">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
              Recent Activity
            </div>
            <h3 className="font-display font-bold text-xl text-zinc-950">Latest Work Orders</h3>
          </div>
          <Link
            to="/work-orders"
            className="text-xs font-bold tracking-wider uppercase text-orange-600 hover:text-orange-700 flex items-center gap-1"
          >
            All <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="divide-y divide-zinc-200">
          {recent.map((w) => (
            <Link
              key={w.id}
              to={`/work-orders/${w.id}`}
              data-testid={`recent-wo-${w.number}`}
              className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3 hover:bg-zinc-50"
            >
              <span className="font-mono text-xs font-bold text-zinc-950 w-20 shrink-0">
                {w.number}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-zinc-950 truncate">{w.title}</div>
                <div className="flex items-center gap-2 mt-1">
                  <TypePill type={w.type} />
                  <PriorityPill priority={w.priority} />
                </div>
              </div>
              <StatusPill status={w.status} />
            </Link>
          ))}
          {recent.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">No work orders yet.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const Mini = ({ label, v }) => (
  <div>
    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">{label}</div>
    <div className="font-mono font-bold text-lg text-zinc-950">{v}</div>
  </div>
);

const CatBar = ({ label, value, total, color }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-zinc-700">{label}</span>
        <span className="font-mono text-zinc-500">{value} · {pct}%</span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
